// START IMPORTS
import { Box, Stack } from '@ltvco/refresh-lib/theme';
import {
  QueryParams,
  FilterTypeOption,
  ReportFilter,
  SortBy,
  SortDirection,
  SearchComponent,
  FiltersComponent,
  SortComponent,
} from '@ltvco/refresh-lib/v2';

import { ChatSearchContainer } from './components';
// END IMPORTS

// START INTERFACES_AND_TYPES
type SearchAndFilterControlsProps = {
  queryParams: QueryParams;
  setQueryParams: any;
  reportTypeFilterOptions: { [key: string]: FilterTypeOption };
  filterTypes: ReportFilter[];
  // eslint-disable-next-line no-unused-vars
  setFilterTypes: (reportTypes: ReportFilter[]) => void;
  sort_by: SortBy;
  sort_direction: SortDirection;
  isInChatPage?: boolean;
  chatSearchWidth?: string;
};
// END INTERFACES_AND_TYPES

const SearchAndFilterControlsStylesInChatPage = {
  searchComponent: {
    width: '100%',
    flexWrap: 'wrap',
    gap: 1.5,
    alignItems: 'flex-start',
    alignContent: 'flex-start',
    '@media (min-width: 1025px)': {
      flexWrap: 'nowrap',
      alignItems: 'center',
    },
  },
  filtersComponent: {
    flexShrink: 0,
    flexWrap: 'nowrap',
    display: 'flex',
    marginLeft: 0,
    paddingLeft: 0,
    maxWidth: '100%',
    minWidth: 0,
    width: '100%',
    '& > *': {
      flex: '1 1 0%',
      minWidth: 0,
    },
    '@media (min-width: 1025px)': {
      marginLeft: 'auto',
      width: 'auto',
      '& > *': {
        flex: '0 0 auto',
      },
    },
    '& .MuiInputBase-root, & .MuiButtonBase-root, & .MuiTypography-root': {
      fontSize: '0.8125rem',
      color: 'text.secondary',
    },
    '& .MuiAutocomplete-root': {
      minWidth: 0,
      maxWidth: '100%',
    },
    '& .MuiOutlinedInput-root': {
      minWidth: '0 !important',
    },
  },
};

export function SearchAndFilterControls({
  queryParams,
  setQueryParams,
  reportTypeFilterOptions,
  filterTypes,
  setFilterTypes,
  sort_by,
  sort_direction,
  isInChatPage,
  chatSearchWidth,
}: SearchAndFilterControlsProps) {
  const isCompactChatToolbar = Boolean(isInChatPage && chatSearchWidth);

  const searchField = (
    <SearchComponent
      queryParams={queryParams}
      setQueryParams={setQueryParams}
    />
  );

  // START RENDER
  return (
    <Stack
      direction={isInChatPage ? 'row' : { xl: 'row', xs: 'column' }}
      spacing={isInChatPage ? 0 : 1.5}
      sx={{
        ...(isInChatPage && {
          ...SearchAndFilterControlsStylesInChatPage.searchComponent,
          ...(isCompactChatToolbar && {
            flexWrap: 'nowrap',
            alignItems: 'center',
            alignContent: 'center',
            gap: 1.5,
            width: '100%',
            minWidth: 0,
          }),
        }),
      }}
    >
      {isInChatPage ? (
        <ChatSearchContainer $fixedWidth={chatSearchWidth}>
          {searchField}
        </ChatSearchContainer>
      ) : (
        <Box>{searchField}</Box>
      )}
      <Stack
        direction="row"
        spacing={1.5}
        {...(isInChatPage && {
          sx: {
            ...SearchAndFilterControlsStylesInChatPage.filtersComponent,
            ...(isCompactChatToolbar && {
              flex: '1 1 0%',
              flexShrink: 1,
              minWidth: 0,
              alignItems: 'center',
              '& .MuiAutocomplete-root': {
                flex: '1 1 0%',
                minWidth: 0,
                width: 'auto !important',
                maxWidth: '100%',
              },
              '& .MuiOutlinedInput-root': {
                minWidth: 0,
              },
              '& .MuiAutocomplete-input': {
                textOverflow: 'ellipsis',
              },
            }),
          },
        })}
      >
        <FiltersComponent
          reportTypeOptions={reportTypeFilterOptions}
          reportTypes={filterTypes}
          setReportTypes={setFilterTypes}
        />
        <SortComponent
          currentSort={{ sort_by, sort_direction }}
          setQueryParams={setQueryParams}
        />
      </Stack>
    </Stack>
  );
  // END RENDER
}
